import type { SortSpec } from './types';

export type SortValue = string | number;

/** How each sortable column reads a row, keyed by column id. */
export type SortReaders<TRow> = Readonly<Record<string, (row: TRow) => SortValue>>;

/**
 * A short list sorted in the browser — every row is already on screen, so there is no server
 * page to ask. Columns without a reader keep their order; text compares case-insensitively.
 */
export function sortRows<TRow>(rows: readonly TRow[], sort: readonly SortSpec[], readers: SortReaders<TRow>): readonly TRow[] {
  if (sort.length === 0) return rows;
  return [...rows].sort((a, b) => {
    for (const spec of sort) {
      const read = readers[spec.columnId];
      if (!read) continue;
      const order = compare(read(a), read(b));
      if (order !== 0) return spec.direction === 'asc' ? order : -order;
    }
    return 0;
  });
}

function compare(left: SortValue, right: SortValue): number {
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  return String(left).localeCompare(String(right), undefined, { sensitivity: 'base', numeric: true });
}
